import React, { useRef, useState } from "react";
import useDismissOnOutside from "../../components/useDismissOnOutside";

// Statuses a non-manager may not move an invoice into or out of.
export const LOCKED = ["FINAL", "SENT", "PAID"];

export const LOCK_MESSAGE =
  "Only managers can change the status of an Issued, Sent, or Paid invoice.";

export function isManager(user) {
  if (!user) return false;
  return Boolean(
    user.is_staff ||
      user.is_superuser ||
      ["ADMIN", "MANAGER"].includes((user.role || "").toUpperCase()) ||
      ["ADMIN", "MANAGER"].includes((user.employee_role || "").toUpperCase())
  );
}

// FINAL is shown as "Issued": numbered, ready to print and send, but not yet
// in QuickBooks. Display only -- the stored value is still FINAL.
const STYLES = {
  DRAFT: { bg: "#f3f4f6", fg: "#4b5563", label: "Draft" },
  FINAL: { bg: "#fef3c7", fg: "#92400e", label: "Issued" },
  SENT:  { bg: "#dbeafe", fg: "#1e40af", label: "Sent" },
  PAID:  { bg: "#dcfce7", fg: "#166534", label: "Paid" },
};

const PILL = {
  borderRadius: 999,
  padding: "3px 12px",
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  border: "none",
  display: "inline-block",
};

const ALL_STATUSES = Object.keys(STYLES);

export default function StatusPill({ status, canChange, onChange, selectableStatuses }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const key = (status || "DRAFT").toUpperCase();
  const s = STYLES[key] || STYLES.DRAFT;
  // Defaults to every status selectable so existing callers (and their
  // tests) keep the current behaviour unchanged.
  const selectable = selectableStatuses || ALL_STATUSES;

  useDismissOnOutside(ref, open, () => setOpen(false));

  if (!canChange) {
    return (
      <span title={LOCK_MESSAGE} style={{ ...PILL, background: s.bg, color: s.fg }}>
        {s.label}
      </span>
    );
  }

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...PILL, background: s.bg, color: s.fg, cursor: "pointer" }}
      >
        {s.label}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute z-40 mt-1 left-0 bg-white border rounded-md shadow-lg py-1 min-w-[110px]"
        >
          {Object.entries(STYLES).map(([value, meta]) => {
            const isSelectable = selectable.includes(value);
            return (
              <button
                key={value}
                role="menuitem"
                type="button"
                disabled={!isSelectable}
                title={isSelectable ? undefined : LOCK_MESSAGE}
                onClick={() => {
                  if (!isSelectable) return;
                  setOpen(false);
                  if (value !== key) onChange(value);
                }}
                className="block w-full text-left px-3 py-1.5 text-sm enabled:hover:bg-gray-50 disabled:cursor-not-allowed"
                style={{
                  fontWeight: value === key ? 600 : 400,
                  color: isSelectable ? undefined : "#9ca3af",
                }}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
